import type { PixelEventName } from '@xshop/shared';
import { dataLayer, injectScript, type BrowserPixel, type PixelPayload } from './loader';

const GTAG_SRC = 'https://www.googletagmanager.com/gtag/js';
const GTM_SRC = 'https://www.googletagmanager.com/gtm.js';
// One gtag.js bundle serves every GA4 property and Google Ads account on the page.
const GTAG_SCRIPT_ID = 'xshop-gtag';

// GA4's own event vocabulary, which Google Ads and GTM consume from the same dataLayer.
const GA_EVENTS: Record<PixelEventName, string> = {
  PageView: 'page_view',
  ViewContent: 'view_item',
  AddToCart: 'add_to_cart',
  InitiateCheckout: 'begin_checkout',
  Purchase: 'purchase'
};

// Pushes one record onto the shared Google dataLayer, which is what every Google tag reads.
function push(record: unknown): void {
  dataLayer().push(record);
}

// Sends a gtag command. gtag.js drains the `arguments` object itself, so an array would be ignored.
const gtag: (...args: unknown[]) => void = function () {
  push(arguments);
};

// Builds the GA4 ecommerce parameters, including transaction_id so GA4 deduplicates purchases.
function gaParameters(event: PixelEventName, payload: PixelPayload): Record<string, unknown> {
  const parameters: Record<string, unknown> = {};
  if (payload.currency) parameters.currency = payload.currency;
  if (payload.value !== undefined) parameters.value = payload.value;
  if (payload.contentId) parameters.items = [{ item_id: payload.contentId, quantity: payload.numItems ?? 1 }];
  if (event === 'Purchase' && payload.orderId) parameters.transaction_id = payload.orderId;
  return parameters;
}

// Loads gtag.js once and configures the ids given, suppressing its automatic page view.
function installGtag(ids: string[]): void {
  if (ids.length === 0) return;
  injectScript(`${GTAG_SRC}?id=${encodeURIComponent(ids[0])}`, GTAG_SCRIPT_ID);
  gtag('js', new Date());
  for (const id of ids) gtag('config', id, { send_page_view: false });
}

// Google Analytics 4: browser-only, since we hold no Measurement Protocol API secret.
export const googleAnalyticsPixel: BrowserPixel = {
  // Configures each GA4 property so our own page_view is the only one it records.
  install(ids) {
    installGtag(ids);
  },

  // Sends one GA4 ecommerce event; purchase carries transaction_id as GA4's deduplication key.
  track(event, payload) {
    gtag('event', GA_EVENTS[event], gaParameters(event, payload));
  }
};

// Google Ads: the remarketing tag loads, but a conversion action needs a per-action label we do
// not collect yet, so nothing is reported as a conversion and the settings page says so.
export const googleAdsPixel: BrowserPixel = {
  // Configures each Google Ads conversion id so remarketing audiences collect.
  install(ids) {
    installGtag(ids);
  },

  // Deliberately silent: firing a conversion without its label would report nothing to Ads.
  track() {
    return;
  }
};

// Google Tag Manager: the container loads and every funnel step reaches it as a dataLayer event.
export const googleTagManagerPixel: BrowserPixel = {
  // Starts each container the merchant configured.
  install(ids) {
    for (const id of ids) {
      push({ 'gtm.start': Date.now(), event: 'gtm.js' });
      injectScript(`${GTM_SRC}?id=${encodeURIComponent(id)}`, `xshop-gtm-${id}`);
    }
  },

  // Publishes the funnel step for whatever tags the merchant configured inside the container.
  track(event, payload) {
    push({ event: GA_EVENTS[event], ...gaParameters(event, payload), xshop_event_id: payload.eventId });
  }
};
